import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { API_BASE_URL } from '../config';
import ThemeSwitcher from './ThemeSwitcher';
import './SiteSettingsEditor.css';

export default function SiteSettingsEditor() {
  const { authenticatedFetch } = useAuth();
  const { settings, refresh } = useSiteSettings();

  const [comingSoonMode, setComingSoonMode] = useState(
    settings?.comingSoonMode ?? false
  );
  const [activeTheme, setActiveTheme] = useState(
    settings?.activeTheme ?? 'WARM_EARTHY'
  );
  const [shopVisible, setShopVisible] = useState(
    settings?.shopVisible ?? true
  );
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  // Sync local form state when settings arrive (first render after fetch) or
  // when the underlying values change. Depend on the primitive fields rather
  // than `settings` itself so a new object reference with identical values
  // doesn't clobber user edits mid-form.
  useEffect(() => {
    if (!settings) return;
    setComingSoonMode(settings.comingSoonMode);
    setActiveTheme(settings.activeTheme);
    setShopVisible(settings.shopVisible);
  }, [settings?.comingSoonMode, settings?.activeTheme, settings?.shopVisible]);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await authenticatedFetch(`${API_BASE_URL}/api/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comingSoonMode, activeTheme, shopVisible }),
      });
      if (!res.ok) throw new Error('Save failed');
      await refresh();
      setStatus({ kind: 'ok', text: 'Saved.' });
    } catch (e) {
      setStatus({ kind: 'err', text: 'Save failed. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="site-settings-editor">
      <h2>Site Settings</h2>

      <section className="setting-block">
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={comingSoonMode}
            onChange={(e) => setComingSoonMode(e.target.checked)}
          />
          <span className="toggle-label">
            Coming Soon mode
            <span className="toggle-hint">
              When on, public visitors see a placeholder page. Admins still see the full site.
            </span>
          </span>
        </label>
      </section>

      <section className="setting-block">
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={shopVisible}
            onChange={(e) => setShopVisible(e.target.checked)}
          />
          <span className="toggle-label">
            Shop visible
            <span className="toggle-hint">
              When off, the "Shop" nav link is hidden and direct visits to /shop redirect to the home page.
            </span>
          </span>
        </label>
      </section>

      <section className="setting-block">
        <ThemeSwitcher
          value={activeTheme}
          onChange={setActiveTheme}
        />
      </section>

      <div className="actions">
        <button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {status && (
          <span className={`status ${status.kind}`}>{status.text}</span>
        )}
      </div>
    </div>
  );
}
